/**
 * Shared client-side formatting helpers.
 *
 * Currently exports date helpers used across due-date display surfaces:
 * pills, kanban cards, task list rows, project cards/rows, dashboard
 * filtering. The driving concern is timezone correctness — see
 * `parseDateLocal` below.
 */

/**
 * Parses a date value into a Date in the user's local timezone.
 *
 * Critical for "YYYY-MM-DD" strings: `new Date("2026-06-09")` parses as UTC
 * midnight, which displays as the previous day for any user west of UTC.
 * This helper detects the date-only form and constructs a local-midnight
 * Date instead.
 *
 * Accepts: ISO date-only, ISO datetime, Date, epoch seconds (number).
 */
export function parseDateLocal(
  value: string | number | Date | null | undefined,
): Date | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value * 1000);
  // YYYY-MM-DD (no time, no Z) → construct local
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return new Date(value);
}

/** Returns a new Date set to local midnight of the given date. */
export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Returns a new Date `n` days after `d` (negative `n` is allowed). */
export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/**
 * Human-readable byte size: 0–1023 → "N B", then "N KB"/"N MB"/"N GB".
 * One decimal place when the number is < 10 in the chosen unit.
 */
export function humanSize(bytes: number): string {
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
}

/**
 * Relative "just now / Nm ago / Nh ago / Nd ago" — coarse, used in file
 * cards and rows where exact times don't matter. Accepts unix seconds, a
 * Date instance, or any value `parseDateLocal` can ingest.
 */
export function timeAgo(
  value: string | number | Date | null | undefined,
): string {
  let seconds: number;
  if (typeof value === "number") {
    // Treat numbers as unix epoch seconds (matches existing FileRow/FileCard).
    seconds = Math.floor(Date.now() / 1000) - value;
  } else {
    const d = parseDateLocal(value);
    if (!d) return "";
    seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  }
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * "Jun 9"-style abbreviated date. Returns "" when the value can't be parsed.
 * Replaces ad-hoc `toLocaleDateString({ month: "short", day: "numeric" })`
 * calls scattered across cards/rows/pills.
 */
export function formatDateShort(
  value: string | number | Date | null | undefined,
): string {
  const d = parseDateLocal(value);
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Due-date proximity classification used by tl-due / tr-due variants.
 *
 *   ""        — no date, or more than 3 days out
 *   "soon"    — due today or within 3 days (still in the future)
 *   "overdue" — due in the past (strictly < today at local midnight)
 *
 * Compares at local-midnight resolution so a task due "today" doesn't flip
 * to overdue mid-afternoon.
 */
export function classifyDue(
  value: string | number | Date | null | undefined,
): "" | "soon" | "overdue" {
  const parsed = parseDateLocal(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return "";
  const today = startOfDay(new Date());
  const day = startOfDay(parsed);
  const diffDays = Math.round(
    (day.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diffDays < 0) return "overdue";
  if (diffDays <= 3) return "soon";
  return "";
}
